/**
 * FormatSelector - Shared component for format selection
 * Used across multiple tools for consistent UI
 */
export class FormatSelector {
  private container: HTMLElement | null = null;
  private selectedFormat: string = '';
  private onFormatChange: ((format: string) => void) | null = null;
  private formats: string[] = [];

  /**
   * Initialize the format selector
   */
  init(
    containerId: string,
    formats: string[],
    defaultFormat: string,
    onFormatChange: (format: string) => void
  ): void {
    this.container = document.getElementById(containerId);
    this.formats = formats;
    this.selectedFormat = defaultFormat;
    this.onFormatChange = onFormatChange;

    if (!this.container) {
      console.error(`Format selector container #${containerId} not found`);
      return;
    }

    this.render();
    this.setupEventListeners();
  }

  /**
   * Render the format selector UI
   */
  private render(): void {
    if (!this.container) return;

    const buttonsHTML = this.formats.map(format => {
      const isActive = format.toLowerCase() === this.selectedFormat.toLowerCase();
      return `<button class="format-option ${isActive ? 'active' : ''}" data-format="${format.toLowerCase()}">${format.toUpperCase()}</button>`;
    }).join('');

    this.container.innerHTML = `
      <div class="format-options">
        ${buttonsHTML}
      </div>
    `;
  }

  /**
   * Setup event listeners for format buttons
   */
  private setupEventListeners(): void {
    if (!this.container) return;

    const buttons = this.container.querySelectorAll('.format-option');
    buttons.forEach(button => {
      button.addEventListener('click', () => {
        const format = button.getAttribute('data-format');
        if (format) {
          this.selectFormat(format);
        }
      });
    });
  }

  /**
   * Select a format
   */
  selectFormat(format: string): void {
    this.selectedFormat = format.toLowerCase();

    // Update button states
    if (this.container) {
      const buttons = this.container.querySelectorAll('.format-option');
      buttons.forEach(button => {
        if (button.getAttribute('data-format') === this.selectedFormat) {
          button.classList.add('active');
        } else {
          button.classList.remove('active');
        }
      });
    }

    // Trigger callback
    if (this.onFormatChange) {
      this.onFormatChange(this.selectedFormat);
    }
  }

  /**
   * Get the currently selected format
   */
  getSelectedFormat(): string {
    return this.selectedFormat;
  }

  /**
   * Reset to default format
   */
  reset(defaultFormat: string): void {
    this.selectFormat(defaultFormat);
  }
}
